import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { RiskBadge } from "../components/RiskBadge";
import { STATUS_LABEL } from "../lib/constants";
import { PushToggle } from "../components/PushToggle";
import { ArrivalFlow } from "../components/case/ArrivalFlow";
import { Building2, Send, FileDown, CheckCircle2, MailCheck, ChevronLeft, ShieldCheck, Eye, EyeOff, Bell, Scale, DollarSign, Users, Loader2, Lock } from "lucide-react";
import { Link as RRLink } from "react-router-dom";
import SubscriptionPlansCard from "../components/subscription/SubscriptionPlansCard";

const UnreadBell = () => {
  const [data, setData] = useState({ unread: 0, cases: [] });
  const [open, setOpen] = useState(false);
  const load = async () => {
    try {
      const r = await api.get("/clinic/unread-count");
      setData(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="relative" data-testid="clinic-unread-bell">
      <button onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-slate-100 transition-colors">
        <Bell className={`w-5 h-5 ${data.unread > 0 ? "text-emerald-700" : "text-slate-500"}`} />
        {data.unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="clinic-unread-count">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Mensagens do Consultor RAF</div>
            <button onClick={load} className="text-[10px] text-blue-700 hover:underline">atualizar</button>
          </div>
          {data.cases.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">Nenhuma mensagem nova nas últimas 24h.</div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {data.cases.map((m, i) => (
                <li key={i} className="p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <div className="text-[11px] text-slate-500 mb-1">
                    {m.sender_name} · {new Date(m.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                  </div>
                  <div className="text-xs text-slate-700 line-clamp-2">{m.content}</div>
                  <Link to={`/clinica/caso/${m.case_id}`} onClick={() => setOpen(false)}
                    className="mt-1 text-[10px] text-emerald-700 hover:underline inline-block">Abrir caso →</Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const ClinicDashboard = () => {
  const [clinic, setClinic] = useState(null);
  const [me, setMe] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewals, setRenewals] = useState([]);
  const [renewalBusy, setRenewalBusy] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/clinic/me"),
      api.get("/clinic/reservations"),
      api.get("/clinic/certificate/renewal/mine").catch(() => ({ data: [] })),
    ])
      .then(([m, r, rn]) => {
        setClinic(m.data.clinic); setMe(m.data.user);
        setReservations(r.data); setRenewals(rn.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const submitRenewal = async (e) => {
    e.preventDefault();
    const file = e.target.doc.files[0];
    if (!file) { toast.error("Selecione o arquivo de renovação"); return; }
    setRenewalBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/clinic/certificate/renewal", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento enviado — admin recebeu para aprovação");
      const rn = await api.get("/clinic/certificate/renewal/mine");
      setRenewals(rn.data);
      e.target.reset();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Erro"); }
    finally { setRenewalBusy(false); }
  };

  const pending = reservations.filter((c) => !c.clinic_response_at);
  const responded = reservations.filter((c) => c.clinic_response_at);
  // Internações = chegadas registradas e já confirmadas pelo consultor.
  // Serve de KPI operacional para a unidade acompanhar quantos casos
  // fecharam o ciclo com internação de fato.
  const admissions = reservations.filter((c) => (c.arrival || {}).confirmed);

  if (loading) return <div className="p-10 text-slate-500">Carregando painel…</div>;

  // Gate de termos comerciais — bloqueia dashboard até aceite formal.
  if (me && !me.terms_accepted_at) {
    return <ClinicTermsGate clinicName={clinic?.name || me?.name} onAccepted={loadAll} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Painel da clínica</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-slate-400" strokeWidth={1.5} /> {clinic?.name || "Reservas recebidas"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Todas as reservas e Dossiês Inteligentes RAF recebidos.</p>
        </div>
        {clinic && (
          <div className="flex items-center gap-3">
            <UnreadBell />
            <div className="text-xs text-slate-500 text-right">
              <div>{clinic.city}, {clinic.state}</div>
              <div className="font-mono">{clinic.phone}</div>
              <div>Vagas: <b>{Math.max(0, clinic.beds_available || 0)}</b></div>
            </div>
          </div>
        )}
      </div>

      <PushToggle audience="clinica" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="clinic-kpi-cards">
        <div className="p-5 rounded-xl bg-white border border-slate-200" data-testid="kpi-pending">
          <div className="text-xs uppercase tracking-widest text-slate-500">Aguardando resposta</div>
          <div className="font-display font-bold text-3xl mt-1 text-orange-600">{pending.length}</div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-slate-200" data-testid="kpi-responded">
          <div className="text-xs uppercase tracking-widest text-slate-500">Respondidas</div>
          <div className="font-display font-bold text-3xl mt-1 text-green-600">{responded.length}</div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-slate-200" data-testid="kpi-reservations">
          <div className="text-xs uppercase tracking-widest text-slate-500">Reservas</div>
          <div className="font-display font-bold text-3xl mt-1 text-[#0B2B62]">{reservations.length}</div>
        </div>
        <div className="p-5 rounded-xl bg-white border border-slate-200" data-testid="kpi-admissions">
          <div className="text-xs uppercase tracking-widest text-slate-500">Internações</div>
          <div className="font-display font-bold text-3xl mt-1 text-emerald-700">{admissions.length}</div>
        </div>
      </div>

      {/* Planos de assinatura Ouro/Prata/Bronze — Iter 68.7 */}
      <SubscriptionPlansCard kind="clinic" entity={clinic} />

      {/* Renovação de credenciamento */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="clinic-renewal-panel">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="font-display font-bold text-lg text-[#0B2B62] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> Renovação de credenciamento
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Anexe alvará, contrato social ou documento equivalente. O admin RAF aprova em 1 clique e estende 12 meses.
            </p>
          </div>
          {clinic?.certification_expires_at && (
            <div className="text-right text-xs">
              <div className="text-slate-500">Válido até</div>
              <div className="font-mono text-[#0B2B62] font-semibold">{new Date(clinic.certification_expires_at).toLocaleDateString("pt-BR")}</div>
            </div>
          )}
        </div>
        <form onSubmit={submitRenewal} className="flex flex-wrap items-center gap-2" data-testid="renewal-form">
          <input type="file" name="doc" required data-testid="renewal-file"
                 className="text-sm border border-slate-300 rounded-md file:mr-3 file:px-3 file:py-1.5 file:bg-slate-50 file:border-0 file:text-sm file:font-semibold file:text-[#0B2B62]" />
          <button type="submit" disabled={renewalBusy} data-testid="renewal-submit"
                  className="px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold disabled:opacity-50">
            {renewalBusy ? "Enviando..." : "Enviar documento"}
          </button>
        </form>
        {renewals.length > 0 && (
          <div className="mt-4 space-y-1.5">
            {renewals.slice(0, 3).map((r) => (
              <div key={r.id} data-testid={`renewal-item-${r.id}`} className="flex items-center justify-between gap-2 text-xs py-1.5 border-t border-slate-100">
                <div className="text-slate-600 truncate">
                  <span className="font-mono">{new Date(r.submitted_at).toLocaleDateString("pt-BR")}</span> · {r.document_filename}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${r.status === "approved" ? "bg-green-100 text-green-800" : r.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                  {r.status === "approved" ? "aprovada" : r.status === "rejected" ? "recusada" : "pendente"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? <p className="text-slate-500">Carregando...</p> : reservations.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl text-slate-500">
          Nenhuma reserva recebida ainda.
        </div>
      ) : (
        <div className="space-y-3">
          {reservations.map((c) => (
            <div key={c.id} data-testid={`clinic-res-${c.id}`}
              className="block bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0B2B62] transition-colors">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <Link to={`/clinica/caso/${c.id}`} className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-slate-500 uppercase tracking-widest">{c.protocol}</div>
                  <h3 className="font-display font-bold text-lg text-[#0B2B62] mt-0.5">{c.patient_name}, {c.patient_age}</h3>
                  <p className="text-sm text-slate-600">{c.city} · {c.substance}</p>
                </Link>
                <div className="flex flex-col items-end gap-1.5">
                  <RiskBadge level={c.risk_level} size="sm" />
                  {c.clinic_response_at ? (
                    <span className="text-xs inline-flex items-center gap-1 text-green-700 font-semibold">
                      <CheckCircle2 className="w-3 h-3" /> Respondida
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">Pendente</span>
                  )}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 gap-2 flex-wrap">
                <span>{STATUS_LABEL[c.status] || c.status}</span>
                <div className="flex items-center gap-3">
                  <span>Enviado {c.clinic_email_sent_at ? new Date(c.clinic_email_sent_at).toLocaleString("pt-BR") : "—"}</span>
                  <a
                    data-testid={`clinic-dir-btn-${c.id}`}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${c.id}/dossier.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 text-slate-700 hover:border-[#0B2B62] hover:text-[#0B2B62] font-semibold"
                  >
                    <FileDown className="w-3.5 h-3.5" /> Baixar DIR
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ClinicCase = () => {
  const { caseId } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/clinic/cases/${caseId}`);
      setC(data);
    } catch { toast.error("Caso não encontrado"); nav("/clinica"); }
  }, [caseId, nav]);
  useEffect(() => { load(); }, [load]);

  const respond = async (accepted) => {
    setBusy(true);
    try {
      const text = msg || (accepted ? "Reserva confirmada. Aguardamos a chegada do paciente." : "Não há vagas compatíveis no momento.");
      await api.post(`/clinic/cases/${caseId}/respond`, { message: text });
      toast.success("Resposta registrada");
      load();
    } catch { toast.error("Erro ao registrar resposta"); }
    finally { setBusy(false); }
  };

  if (!c) return <div className="p-10 text-slate-500">Carregando...</div>;
  const d = c.dossier;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <Link to="/clinica" className="text-sm text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Voltar ao painel
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="text-xs font-mono text-slate-500 uppercase tracking-widest">{c.protocol}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">{c.patient_name}, {c.patient_age}</h1>
          <div className="text-sm text-slate-600 mt-1">{c.city} · {c.substance}</div>
        </div>
        <div className="flex items-center gap-2">
          <RiskBadge level={c.risk_level} size="lg" />
          <button data-testid="clinic-dir-preview-toggle"
            onClick={() => setPreviewOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#0B2B62] bg-[#0B2B62] text-white text-sm hover:bg-slate-800">
            {previewOpen ? <><EyeOff className="w-4 h-4" /> Fechar prévia</> : <><Eye className="w-4 h-4" /> Prévia do DIR</>}
          </button>
          <a data-testid="clinic-pdf" href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/dossier.pdf`}
             target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-sm hover:bg-slate-50">
            <FileDown className="w-4 h-4" /> Baixar DIR
          </a>
        </div>
      </div>

      {previewOpen && (
        <div className="mb-6 border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="clinic-dir-preview">
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center justify-between gap-2">
            <span>Prévia do Dossiê Inteligente RAF — link assinado, expira em 72h</span>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/dossier.pdf`}
               target="_blank" rel="noreferrer"
               className="text-[#0B2B62] hover:underline font-semibold">Abrir em nova aba</a>
          </div>
          <iframe
            title="DIR preview"
            data-testid="clinic-dir-iframe"
            src={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/dossier.pdf#toolbar=0&navpanes=0&view=FitH`}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            className="w-full h-[70vh] border-0"
          />
        </div>
      )}

      {c.dossier_signature && (
        <div className="mb-4 p-4 rounded-xl border border-green-200 bg-green-50 text-sm">
          <div className="flex items-center gap-2 text-green-700 font-semibold">
            <ShieldCheck className="w-4 h-4" /> Dossiê assinado digitalmente
          </div>
          <div className="mt-1 text-slate-700">
            <div>{c.dossier_signature.consultor_name} · {new Date(c.dossier_signature.signed_at).toLocaleString("pt-BR")}</div>
            <div className="text-xs font-mono text-slate-500 break-all mt-1">SHA-256: {c.dossier_signature.hash}</div>
          </div>
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">Dossiê Inteligente RAF</div>
        <h2 className="font-display font-bold text-2xl text-[#0B2B62] mt-1 mb-4">Dados clínicos do caso</h2>
        {d ? (
          <div className="space-y-4 text-sm">
            <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
              <div className="text-xs uppercase text-slate-500 tracking-widest">Justificativa</div>
              <p className="text-slate-700 mt-1">{d.risk_justification}</p>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-500 tracking-widest mb-1">Resumo clínico</div>
              <p className="text-slate-700 whitespace-pre-line">{d.clinical_summary}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase text-slate-500 tracking-widest mb-1">Perfil</div>
                <p className="text-slate-700">{d.dependency_profile}</p>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500 tracking-widest mb-1">Modalidade sugerida</div>
                <p className="text-slate-700 capitalize">{d.recommended_modality}</p>
              </div>
            </div>
          </div>
        ) : <p className="text-slate-500 text-sm">Dossiê ainda não disponível.</p>}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        {c.clinic_response_at ? (
          <div>
            <div className="flex items-center gap-2 text-green-700 font-semibold">
              <MailCheck className="w-5 h-5" /> Resposta registrada em {new Date(c.clinic_response_at).toLocaleString("pt-BR")}
            </div>
            <p className="mt-2 text-sm text-slate-700">"{c.clinic_response_message || 'Sem mensagem'}"</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="font-display font-bold text-xl text-[#0B2B62]">Responder ao consultor</h3>
            <textarea data-testid="clinic-msg" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
              placeholder="Mensagem opcional para o consultor..." className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
            <div className="flex gap-2">
              <button data-testid="clinic-accept" disabled={busy} onClick={() => respond(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                Confirmar reserva
              </button>
              <button data-testid="clinic-decline" disabled={busy} onClick={() => respond(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50 disabled:opacity-50">
                Sem vaga no momento
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Fluxo de chegada — aparece assim que a reserva está confirmada
          (status=reserva_confirmada) OU já há um arrival registrado. */}
      {(c.status === "reserva_confirmada" || c.arrival) && (
        <ArrivalFlow caseObj={c} role="clinica" onDone={load} />
      )}
    </div>
  );
};

/**
 * ClinicTermsGate — Gate de aceite dos termos comerciais RAF para clínicas.
 * Iter 68.16: consentimento de divulgação + critérios de aprovação + comissão
 * 100% da matrícula (independente do plano da família). Diferente do
 * profissional (que atende via videochamada), na clínica paciente e família
 * comparecem PRESENCIALMENTE para tratamento.
 */
const ClinicTermsGate = ({ clinicName, onAccepted }) => {
  const [accepted, setAccepted] = useState(false);
  const [consentDivulgacao, setConsentDivulgacao] = useState(false);
  const [consentCriterios, setConsentCriterios] = useState(false);
  const [consentComissao, setConsentComissao] = useState(false);
  const [consentLgpd, setConsentLgpd] = useState(false);
  const [consentConfidentiality, setConsentConfidentiality] = useState(false);
  const [saving, setSaving] = useState(false);

  const allChecked = accepted && consentDivulgacao && consentCriterios && consentComissao && consentLgpd && consentConfidentiality;

  const accept = async () => {
    if (!allChecked) {
      toast.error("Marque todas as caixas de consentimento antes de continuar.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clinic/me/accept-terms", {
        consent_lgpd: consentLgpd,
        consent_confidentiality: consentConfidentiality,
        consent_terms: accepted,
      });
      toast.success("Termos aceitos. Bem-vindo(a) à rede RAF Direct!");
      onAccepted && onAccepted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar aceite");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="clinic-terms-gate">
      <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1.5">
        <Scale className="w-3.5 h-3.5" /> Termos comerciais · Unidade Parceira RAF Direct
      </div>
      <h1 className="font-display font-bold text-[#0B2B62] text-3xl mt-1">
        Olá, {clinicName}. Leia e aceite antes de continuar.
      </h1>
      <p className="text-slate-600 mt-2 max-w-2xl leading-relaxed text-left">
        Estes termos regem sua relação como <b>Unidade Parceira RAF Direct</b>. Sem o aceite
        formal, seu cadastro permanece incompleto — a unidade não aparece na vitrine e
        nenhum caso é encaminhado.
      </p>

      <div className="mt-6 space-y-4">
        {/* Consentimento LGPD dedicado (Art. 7 + Art. 11 + Termo de Confidencialidade) */}
        <div className="p-5 rounded-lg border-2 border-emerald-500 bg-emerald-50/60">
          <div className="font-display font-bold text-emerald-900 flex items-center gap-2">
            <Lock className="w-5 h-5" /> Conformidade LGPD e Sigilo Clínico
          </div>
          <p className="text-sm text-emerald-900 mt-2 leading-relaxed">
            Reconheço que a Unidade Parceira trata dados pessoais e <b>dados sensíveis de saúde</b>
            (histórico, diagnósticos, medicações) das famílias direcionadas pela RAF, e me
            comprometo a fazê-lo em conformidade com a <b>LGPD (Lei 13.709/2018, Art. 7 e Art. 11)</b>
            e com o <RRLink to="/termo-de-confidencialidade" target="_blank" className="underline">
            Termo de Confidencialidade</RRLink> da plataforma.
          </p>
          <div className="mt-3 space-y-2">
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={consentLgpd}
                     onChange={(e) => setConsentLgpd(e.target.checked)}
                     data-testid="clinic-terms-lgpd" className="mt-1" />
              <span className="text-xs text-emerald-900 font-semibold">
                Aceito tratar os dados dos pacientes/famílias em estrita conformidade com a LGPD (Art. 7 e Art. 11).
              </span>
            </label>
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={consentConfidentiality}
                     onChange={(e) => setConsentConfidentiality(e.target.checked)}
                     data-testid="clinic-terms-confidentiality" className="mt-1" />
              <span className="text-xs text-emerald-900 font-semibold">
                Li e aceito integralmente o Termo de Confidencialidade, mantendo sigilo absoluto sobre dados clínicos.
              </span>
            </label>
          </div>
        </div>

        {/* Modelo operacional */}
        <div className="p-5 rounded-lg border-2 border-[#0B2B62] bg-slate-50">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2">
            <Users className="w-5 h-5" /> Modelo de atendimento (importante)
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            Na Unidade Parceira, o <b>paciente e a família comparecem PRESENCIALMENTE</b> para
            internação, hospital-dia ou comunidade terapêutica na sua clínica. Diferente do
            profissional credenciado (que atende via videochamada dentro da plataforma), aqui
            a RAF <b>faz apenas a triagem, o direcionamento e a intermediação inicial</b> —
            o tratamento em si acontece dentro da sua estrutura física.
          </p>
        </div>

        {/* Comissão 100% da matrícula */}
        <div className="p-5 rounded-lg border-2 border-emerald-500 bg-emerald-50">
          <div className="font-display font-bold text-emerald-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5" /> Comissão RAF · 100% do valor da matrícula
          </div>
          <p className="text-sm text-emerald-900 mt-2 leading-relaxed">
            Reconheço que <b>100% (cem por cento) do valor da matrícula</b> de cada paciente
            direcionado pela RAF Direct fica com a plataforma como remuneração pelos serviços
            de triagem inteligente, dossiê clínico, direcionamento seguro e comissão do
            consultor RAF que acompanhou o caso. Esta comissão é <b>fixa e independente do
            plano escolhido pela família</b> (mensalidade, valor final do tratamento ou
            modalidade). A mensalidade do tratamento em si é acordada <b>diretamente entre a
            clínica e a família</b> e não sofre split RAF.
          </p>
          <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={consentComissao}
                   onChange={(e) => setConsentComissao(e.target.checked)}
                   data-testid="clinic-terms-comissao" className="mt-1" />
            <span className="text-xs text-emerald-900 font-semibold">
              Aceito que 100% do valor da matrícula fica com a RAF, independentemente do plano
              da família.
            </span>
          </label>
        </div>

        {/* Consentimento de divulgação */}
        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" /> Consentimento de divulgação
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            Autorizo a RAF Direct a <b>divulgar publicamente</b> minha unidade parceira
            (nome fantasia, cidade/UF, fotos internas/externas, modalidades atendidas,
            público-alvo, diferenciais terapêuticos e código anonimizado) na vitrine
            pública, em campanhas, materiais institucionais, redes sociais e páginas de
            impacto da plataforma. Dados sensíveis (CNPJ completo, endereço, contatos
            diretos) permanecem restritos ao painel do consultor RAF.
          </p>
          <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={consentDivulgacao}
                   onChange={(e) => setConsentDivulgacao(e.target.checked)}
                   data-testid="clinic-terms-divulgacao" className="mt-1" />
            <span className="text-xs text-slate-800 font-semibold">
              Autorizo a divulgação pública da minha unidade nos canais RAF Direct.
            </span>
          </label>
        </div>

        {/* Critérios de aprovação */}
        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-700" /> Critérios de aprovação
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            Comprometo-me a atender aos <b>critérios mínimos</b> exigidos pela RAF Direct para
            manter o credenciamento ativo:
          </p>
          <ul className="text-sm text-slate-700 mt-2 ml-4 list-disc space-y-1">
            <li>Alvará sanitário e/ou licença de funcionamento vigente</li>
            <li>CNPJ ativo e em situação regular na Receita Federal</li>
            <li>Equipe multidisciplinar mínima (médico responsável técnico + psicólogo + enfermagem)</li>
            <li>Prontuário digital ou físico organizado + LGPD-compliance</li>
            <li>Contrato terapêutico formal com paciente/família antes da internação</li>
            <li>Comunicação em até 24h com o consultor RAF sobre chegada, alta e ocorrências</li>
            <li>Renovação anual de documentos via painel (o admin aprova em 1 clique)</li>
            <li>Nota mínima 4/5 nas avaliações NPS das famílias (revisado a cada 90 dias)</li>
          </ul>
          <p className="text-xs text-slate-500 mt-3 italic">
            Descumprimento pode acarretar suspensão do selo de credenciamento, pausa nos
            direcionamentos e/ou desligamento definitivo da rede RAF.
          </p>
          <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={consentCriterios}
                   onChange={(e) => setConsentCriterios(e.target.checked)}
                   data-testid="clinic-terms-criterios" className="mt-1" />
            <span className="text-xs text-slate-800 font-semibold">
              Comprometo-me a manter todos os critérios acima durante o credenciamento.
            </span>
          </label>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-lg border border-amber-300 bg-amber-50">
        <label className="inline-flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={accepted}
                 onChange={(e) => setAccepted(e.target.checked)}
                 data-testid="clinic-terms-final" className="mt-1" />
          <span className="text-sm text-amber-900 font-semibold leading-relaxed">
            Li integralmente e <b>aceito</b> todos os termos acima. Estou ciente de que o
            aceite fica registrado com data/hora para fins contratuais e auditoria RAF.
          </span>
        </label>
      </div>

      <button onClick={accept} disabled={!allChecked || saving}
              data-testid="clinic-terms-accept-btn"
              className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Aceitar termos e continuar cadastro
      </button>
    </div>
  );
};
